import type { ExportColumnInDTO } from '@api/ringil-api.v2';
import type { FieldKey, ViewColumn } from '../views/types';
import type { FieldMeta, Translate } from './columnModel';
import { fieldAccess, visibleColumns } from './fieldAccess';

/** The backend's limits on `ExportJobRequestInDTO.columns`. */
export const MAX_EXPORT_COLUMNS = 200;
export const MAX_EXPORT_HEADER_LENGTH = 120;

/**
 * What the export writes for the open tab: one export column per field, in display order (column
 * order, then the order inside a stack), so a stacked "Route" becomes its loading and unloading
 * columns side by side.
 *
 * A single-field column keeps the user's own title; every other field is named by its translated
 * header. Fields the user may not see and controls (`exportable: false`) are left out — the backend
 * would drop them too, but the user should not wonder where they went. A tab without columns shows
 * the table's defaults, so those are exported.
 */
export const exportColumns = (columns: ViewColumn[], fields: FieldMeta[], defaultColumns: FieldKey[][], tr: Translate): ExportColumnInDTO[] => {
  const byKey = new Map(fields.map(field => [field.key, field]));
  const source = columns.length > 0 ? columns : defaultColumns.map((members, index) => ({ id: `default-${index}`, members }));
  const seen = new Set<FieldKey>();
  const result: ExportColumnInDTO[] = [];
  visibleColumns(source, fieldAccess(fields)).forEach(column => {
    const members = column.members.filter(key => byKey.get(key)?.exportable !== false && !seen.has(key));
    members.forEach(key => {
      seen.add(key);
      const field = byKey.get(key);
      const header = column.title && members.length === 1 ? column.title : tr(field.header, field.headerDefault);
      result.push({ field: key, header: header.slice(0, MAX_EXPORT_HEADER_LENGTH) });
    });
  });
  return result.slice(0, MAX_EXPORT_COLUMNS);
};
